import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { useAuth } from '../../../contexts/AuthContext'
import { api, LinkedInRepoComment } from '../../../services/api'

/**
 * Research notes on a repo.
 *
 * Assigning a repo to someone is only worth it if the write-up lands where the
 * next person can find it; there was nowhere to put it, so findings lived in
 * chats and were lost.
 */

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const Title = styled.h4`
  margin: 0;
  font-size: 0.95rem;
`

const Note = styled.div`
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.6rem 0.8rem;
`

const NoteMeta = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.35rem;
`

const Body = styled.div`
  white-space: pre-wrap;
  font-size: 0.9rem;
`

const Editor = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.6rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid rgba(0, 0, 0, 0.15);
  font: inherit;
  resize: vertical;
`

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
`

const Button = styled.button`
  padding: 0.45rem 1rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Link = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  padding: 0;
`

const Problem = styled.p`
  color: #b3261e;
  font-size: 0.85rem;
  margin: 0;
`

interface RepoCommentsProps {
    repoId: number
}

export const RepoComments: React.FC<RepoCommentsProps> = ({ repoId }) => {
    // Read the viewer here rather than threading a prop down through the modal:
    // only the delete affordance depends on it.
    const { user } = useAuth()
    const currentUserId = user?.id ?? null
    const [comments, setComments] = useState<LinkedInRepoComment[]>([])
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const [problem, setProblem] = useState<string | null>(null)

    const load = () => {
        api.linkedin
            .getRepoComments(repoId)
            .then((res) => setComments(res.data))
            .catch(() => setProblem('No se pudieron cargar las notas.'))
    }

    useEffect(load, [repoId])

    const submit = async () => {
        const body = draft.trim()
        if (!body) return
        setSaving(true)
        setProblem(null)
        try {
            await api.linkedin.createRepoComment(repoId, body)
            setDraft('')
            load()
        } catch {
            setProblem('No se pudo guardar la nota. Vuelve a intentarlo.')
        } finally {
            setSaving(false)
        }
    }

    const remove = async (commentId: number) => {
        setProblem(null)
        try {
            await api.linkedin.deleteRepoComment(repoId, commentId)
            load()
        } catch {
            setProblem('No se pudo borrar la nota.')
        }
    }

    return (
        <Wrap>
            <Title>Notas de investigación ({comments.length})</Title>

            {comments.map((c) => (
                <Note key={c.id}>
                    <Body>{c.body}</Body>
                    <NoteMeta>
                        <span>
                            {c.user_display_name || c.username || 'Alguien'} ·{' '}
                            {new Date(c.created_at).toLocaleDateString('es-ES')}
                        </span>
                        {currentUserId != null && c.user_id === currentUserId && (
                            <Link onClick={() => remove(c.id)}>Borrar</Link>
                        )}
                    </NoteMeta>
                </Note>
            ))}

            {comments.length === 0 && <Body style={{ opacity: 0.7 }}>Todavía no hay notas sobre este repo.</Body>}

            <Editor
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Qué has aprendido de este repo, qué merece la pena, qué no…"
            />
            {problem && <Problem>{problem}</Problem>}
            <Row>
                <Button onClick={submit} disabled={saving || !draft.trim()}>
                    {saving ? 'Guardando…' : 'Añadir nota'}
                </Button>
            </Row>
        </Wrap>
    )
}

export default RepoComments
